import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const links = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/parts', label: 'Parts' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const styles = `
  .navbar {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 1000;
    background: rgba(13,13,13,0.94);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid rgba(255,255,255,0.06);
    transition: box-shadow 0.3s;
  }
  .navbar--scrolled {
    box-shadow: 0 2px 32px rgba(0,0,0,0.6);
    background: rgba(13,13,13,0.99);
  }
  .navbar__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 24px;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
  .navbar__logo {
    display: flex;
    align-items: center;
    gap: 13px;
    text-decoration: none;
    flex-shrink: 0;
  }
  .navbar__logo img {
    height: 50px;
    width: 50px;
    object-fit: contain;
  }
  .navbar__brand-name {
    font-family: 'Oswald', sans-serif;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #fff;
    display: block;
    line-height: 1;
  }
  .navbar__brand-sub {
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #d4a017;
    margin-top: 4px;
    display: block;
  }
  .navbar__links {
    display: flex;
    align-items: center;
    gap: 2px;
    list-style: none;
    margin: 0; padding: 0;
  }
  .navbar__links a {
    font-family: 'Oswald', sans-serif;
    font-size: 0.83rem;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.7);
    padding: 8px 14px;
    display: block;
    position: relative;
    transition: color 0.2s;
    text-decoration: none;
  }
  .navbar__links a::after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 14px; right: 14px;
    height: 2px;
    background: #c0392b;
    transform: scaleX(0);
    transition: transform 0.22s;
  }
  .navbar__links a:hover,
  .navbar__links a.active { color: #fff; }
  .navbar__links a.active::after,
  .navbar__links a:hover::after { transform: scaleX(1); }
  .navbar__phone {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #c0392b;
    color: #fff;
    padding: 9px 18px;
    font-family: 'Oswald', sans-serif;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-decoration: none;
    transition: background 0.2s;
    flex-shrink: 0;
  }
  .navbar__phone:hover { background: #96281b; }
  .navbar__hamburger {
    display: none;
    background: none;
    color: #fff;
    padding: 4px;
    flex-shrink: 0;
  }
  .navbar__mobile {
    display: none;
    flex-direction: column;
    background: #161616;
    border-top: 1px solid rgba(255,255,255,0.07);
    padding: 12px 0 20px;
  }
  .navbar__mobile.open { display: flex; }
  .navbar__mobile a {
    font-family: 'Oswald', sans-serif;
    font-size: 1.05rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.8);
    padding: 14px 28px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    transition: color 0.2s, background 0.2s;
    text-decoration: none;
    display: block;
  }
  .navbar__mobile a:hover,
  .navbar__mobile a.active { color: #fff; background: rgba(192,57,43,0.12); }
  .navbar__mobile-phone {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 28px 0;
    font-family: 'Oswald', sans-serif;
    font-size: 1rem;
    color: #d4a017;
    letter-spacing: 0.06em;
    text-decoration: none;
  }
  @media (max-width: 900px) {
    .navbar__links, .navbar__phone { display: none !important; }
    .navbar__hamburger { display: flex !important; }
  }
`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <style>{styles}</style>
      <header className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
        <div className="navbar__inner">
          <Link to="/" className="navbar__logo">
            <img src="/logo.png" alt="Wengers Cycle Works" />
            <div>
              <span className="navbar__brand-name">Wengers Cycle Works</span>
              <span className="navbar__brand-sub">New Holland, PA</span>
            </div>
          </Link>

          <nav>
            <ul className="navbar__links">
              {links.map(l => (
                <li key={l.to}>
                  <Link to={l.to} className={location.pathname === l.to ? 'active' : ''}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <a href="tel:7172785897" className="navbar__phone">
            <Phone size={14} /> (717) 278-5897
          </a>

          <button className="navbar__hamburger" onClick={() => setOpen(o => !o)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <div className={`navbar__mobile${open ? ' open' : ''}`}>
          {links.map(l => (
            <Link key={l.to} to={l.to} className={location.pathname === l.to ? 'active' : ''}>
              {l.label}
            </Link>
          ))}
          <a href="tel:7172785897" className="navbar__mobile-phone">
            <Phone size={16} /> (717) 278-5897
          </a>
        </div>
      </header>
    </>
  );
}
